import { useQuery } from '@tanstack/react-query';
import http from 'helpers/requests';

/**
 * @typedef {Object} ConsultantClient
 * @property {string} id                       operator `_id`
 * @property {string} name
 * @property {string|undefined} organizationId Clerk org id; falsy → openClient refuses
 * @property {{name: string, iso2: string|null}|null} country  null when unknown
 * @property {number} installationsCount
 * @property {number} productCount             emission factors
 * @property {number} diagramsCount
 * @property {string|null} lastUpdatedAt       ISO timestamp or null
 */

/**
 * Hook to fetch the consultant's clients (operator orgs), mapped to the shape
 * the consultant home renders. Errors surface through the query's `error` —
 * the page owns the error state (TanStack v5 has no per-query `onError`).
 *
 * @returns {import('@tanstack/react-query').UseQueryResult<ConsultantClient[]>}
 */
const useFetchConsultantClients = () => {
  return useQuery({
    queryKey: ['consultantClients'],
    queryFn: async () => {
      const response = await http.get('/consultants/operators');
      return (response.data?.operators ?? []).map((operator) => ({
        id: operator._id,
        name: operator.name,
        organizationId: operator.clerkOrganisationId, // Clerk organization ID for switching context
        // null when unknown — display fallbacks live in the components, not the cache
        country: operator.address?.country
          ? { name: operator.address.country.name, iso2: operator.address.country.iso2 || null }
          : null,
        installationsCount: operator.installationsCount || 0,
        productCount: operator.emissionFactorsCount || 0, // Products = emission factors
        diagramsCount: operator.diagramsCount || 0, // Diagrams count
        lastUpdatedAt: operator.updatedAt || null, // ISO timestamp; formatted in local time at render
      }));
    },
    staleTime: 30_000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: true,
  });
};

export default useFetchConsultantClients;
